import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Camera as CameraIcon, X } from 'lucide-react';
import { Button } from '@shared/ui/primitives/Button';
import { TextAreaField, TextInputField } from '@shared/ui/forms/FormFields';
import { toast } from '@shared/ui/feedback/Toast';
import { platform } from '@shared/lib/platform';
import { cn } from '@shared/lib/cn';
import { createGoodsRemoval } from '../memberAppApi';
import { isoDay, startOfToday } from '../visitorAccessFormat';
import { FIELD, INPUT } from '../visitorAccessStyles';
import { VisitDates } from '../VisitDates';
import { BookingField, BookingSteps, PropertyField, useCreatePin, type BookingProps } from './BookingForm';

/** The gate takes one photo of up to 10 MB. */
const PHOTO_MAX_BYTES = 10 * 1024 * 1024;

const STEPS = [
  { label: 'Goods', title: 'What is leaving?', description: 'The guard checks the goods against this and the photo.' },
  { label: 'Who and when', title: 'Who is taking it, and when?', description: 'The PIN works for that one day.' },
  { label: 'More', title: 'Anything else?', description: 'A reference number is optional.' },
];

/** A PIN for someone leaving the property with goods, with a photo the guard checks them against. */
export function GoodsRemovalBooking({ properties, onCreated, lead }: BookingProps): JSX.Element {
  // 1. Starts today, at the member's first property.
  const [step, setStep] = useState(0);
  const [locationId, setLocationId] = useState(properties[0]!.location_id);
  const [personName, setPersonName] = useState('');
  const [day, setDay] = useState(startOfToday);
  const [description, setDescription] = useState('');
  const [photo, setPhoto] = useState<{ file: File; url: string } | null>(null);
  const [reference, setReference] = useState('');
  const [photoBusy, setPhotoBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mutation = useCreatePin(createGoodsRemoval, onCreated);

  // 2. A photo over the gate's size limit is turned away.
  const addPhoto = (file: File) => {
    if (file.size > PHOTO_MAX_BYTES) {
      toast.danger('Photo too large', 'The photo must be 10 MB or smaller.');
      return;
    }
    setPhoto({ file, url: URL.createObjectURL(file) });
  };

  // 3. Native: the camera or gallery prompt. Web: the hidden file input.
  const pickPhoto = async () => {
    if (!platform.isNative()) {
      fileInputRef.current?.click();
      return;
    }
    setPhotoBusy(true);
    try {
      const shot = await Camera.getPhoto({ quality: 80, resultType: CameraResultType.Uri, source: CameraSource.Prompt });
      if (shot.webPath) {
        const blob = await fetch(shot.webPath).then((response) => response.blob());
        addPhoto(new File([blob], `goods-${Date.now()}.${shot.format}`, { type: blob.type }));
      }
    } catch (error) {
      toast.danger("Couldn't add photo", error instanceof Error ? error.message : undefined);
    } finally {
      setPhotoBusy(false);
    }
  };

  const onFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) addPhoto(file);
    // Cleared so the same file can be picked again after removing it.
    event.target.value = '';
  };

  const removePhoto = () => {
    if (photo) URL.revokeObjectURL(photo.url);
    setPhoto(null);
  };

  // 4. The preview still showing is freed once the sheet closes.
  const photoRef = useRef(photo);
  photoRef.current = photo;
  useEffect(() => () => {
    if (photoRef.current) URL.revokeObjectURL(photoRef.current.url);
  }, []);

  // 5. Each step's button waits for what that step needs: the goods with a photo, then who takes them.
  const stepReady = [Boolean(description.trim() && photo), Boolean(personName.trim()), true][step];
  const submit = () =>
    mutation.mutate({
      location_id: locationId,
      date: isoDay(day),
      person_name: personName.trim(),
      description: description.trim(),
      reference: reference.trim() || undefined,
      photo: photo!.file,
    });

  return (
    <BookingSteps
      lead={lead}
      steps={STEPS}
      step={step}
      onStep={setStep}
      action={
        step < STEPS.length - 1 ? (
          <Button width="full" disabled={!stepReady} onClick={() => setStep(step + 1)}>
            Continue
          </Button>
        ) : (
          <Button width="full" loading={mutation.isPending} onClick={submit}>
            Create PIN
          </Button>
        )
      }
    >
      {/* 6. Goods: what is leaving and the photo the guard sees. */}
      {step === 0 ? (
        <>
          <TextAreaField
            label="What is leaving"
            rows={2}
            className={FIELD}
            textareaClassName={cn(INPUT, 'min-h-[110px] resize-none py-3')}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
          <BookingField label="Photo" helper="The guard sees this photo.">
            <div className="flex flex-wrap gap-2.5">
              {photo ? (
                <div className="relative">
                  <img src={photo.url} alt="Photo of the goods" className="h-[76px] w-[76px] rounded-xl bg-hairline-strong object-cover" />
                  <button
                    type="button"
                    aria-label="Remove photo"
                    onClick={removePhoto}
                    className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-pill bg-danger text-white"
                  >
                    <X className="h-3 w-3" strokeWidth={2} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  aria-label="Add a photo"
                  disabled={photoBusy}
                  onClick={() => void pickPhoto()}
                  className="flex h-[76px] w-[76px] flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-primary-500 bg-card text-xs font-semibold text-primary-500 disabled:opacity-60"
                >
                  <CameraIcon className="h-5 w-5" strokeWidth={2} />
                  Add
                </button>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onFileSelected} />
          </BookingField>
        </>
      ) : null}

      {/* 7. Who and when: the person taking the goods, and the one day the PIN works. */}
      {step === 1 ? (
        <>
          <TextInputField
            label="Who is taking it"
            className={FIELD}
            inputClassName={INPUT}
            value={personName}
            onChange={(event) => setPersonName(event.target.value)}
          />
          <VisitDates dayLabel="Day" start={day} onStart={setDay} days={1} />
        </>
      ) : null}

      {/* 8. More: a reference for the guard, and the property. */}
      {step === 2 ? (
        <>
          <TextInputField
            label="Reference number"
            placeholder="Optional"
            className={FIELD}
            inputClassName={INPUT}
            value={reference}
            onChange={(event) => setReference(event.target.value)}
          />
          <PropertyField properties={properties} value={locationId} onChange={setLocationId} />
        </>
      ) : null}
    </BookingSteps>
  );
}
